import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, ArrowRight, PartyPopper, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccess({ navigate }) {
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId || !token) {
      setStatus('error');
      return;
    }

    const poll = async (sid, attempt) => {
      if (attempt >= 10) {
        setStatus('timeout');
        return;
      }
      try {
        const res = await fetch(`${API}/api/stripe/checkout-status/${sid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setPaymentData(data);
        setAttempts(attempt + 1);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data so billingRequired is updated
          refreshUser();
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setTimeout(() => poll(sid, attempt + 1), 2000);
      } catch {
        setTimeout(() => poll(sid, attempt + 1), 2500);
      }
    };

    poll(sessionId, 0);
  }, [token]);

  const trialEndDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  })();

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
        data-testid="billing-success-page"
      >
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto" />
            <h2 className="text-xl font-medium text-white">
              {attempts > 4 ? 'Still working...' : 'Confirming your payment...'}
            </h2>
            <p className="text-zinc-500 text-sm">
              {attempts > 4
                ? "If you're not redirected in a few seconds, click below."
                : `This may take a moment. (${attempts}/10)`}
            </p>
            {attempts > 4 && (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white mt-2"
                data-testid="billing-manual-continue"
              >
                Continue to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <PartyPopper className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-white mb-2">Your free trial has started!</h2>
              <p className="text-zinc-400 leading-relaxed">
                Welcome to D3 Command. Your 14-day trial is now active.<br />
                Start turning your meetings into execution.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl border border-white/10 p-4 text-left space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Plan</span>
                <span className="text-white font-medium">{paymentData?.plan_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Billing</span>
                <span className="text-white capitalize">{paymentData?.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Trial ends</span>
                <span className="text-white">{trialEndDate}</span>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white w-full"
              data-testid="billing-go-to-dashboard"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-white mb-2">
                {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Issue'}
              </h2>
              <p className="text-zinc-400">
                {status === 'timeout'
                  ? "We couldn't confirm your payment yet. If you were charged, your subscription will be activated shortly."
                  : 'Something went wrong with your payment. Please try again.'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
                data-testid="billing-error-dashboard"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white"
                data-testid="billing-error-home"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
